// Manage Drivers — retailer portal page.
//
// Source-of-truth equivalent: portal-standalone.html#page-drivers (lines
// 2599-2618 markup, 31840-32100 logic).
//
// Backend (kitchenos-api):
//   GET  /api/admin/drivers           — roster (delivery addon)
//   POST /api/drivers/create          — create driver (driver_portal module)
//   GET  /api/admin/drivers/:id/location — fresh GPS fix (delivery addon)
// No update / delete / toggle-active endpoints exist server-side yet — those
// actions are stubbed below with TODOs and surface a toast for now.
//
// The chrome owns auth, navigation and toasts; this module only renders into
// its assigned container and tears itself down on cleanup.

import { ApiError } from "@tkos/api-client";
import { confirmDialog } from "@tkos/shared-ui";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";
import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface DriverVehicle {
  make?: string;
  model?: string;
  reg?: string;
  colour?: string;
}

interface Driver {
  id: string;
  name: string;
  email?: string;
  phone: string;
  vehicle: DriverVehicle | null;
  active?: boolean;
  online?: boolean;
  rating?: number | null;
  deliveriesCompleted?: number;
}

interface DriversResponse {
  ok: boolean;
  drivers: Driver[];
}

interface CreateDriverResponse {
  ok: boolean;
  driver?: Driver;
  smsSent?: boolean;
  error?: string;
}

type FilterValue = "all" | "active" | "inactive" | "with_jobs";

// ── Helpers ──────────────────────────────────────────────────────────────────

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((part) => part[0] ?? "")
      .filter(Boolean)
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

function isActive(driver: Driver): boolean {
  // Server-side schema doesn't always set `active`; default to true.
  return driver.active !== false;
}

function vehicleLabel(driver: Driver): string {
  const v = driver.vehicle;
  if (!v) return "";
  const model = (v.model || v.make || "").trim();
  const reg = (v.reg || "").trim();
  if (model && reg) return `${model} · ${reg}`;
  return model || reg;
}

function matchesSearch(driver: Driver, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = [
    driver.name,
    driver.phone,
    driver.email ?? "",
    driver.vehicle?.model ?? "",
    driver.vehicle?.make ?? "",
    driver.vehicle?.reg ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function matchesFilter(driver: Driver, filter: FilterValue): boolean {
  switch (filter) {
    case "active":
      return isActive(driver);
    case "inactive":
      return !isActive(driver);
    case "with_jobs":
      // No backend endpoint exposes assigned-job counts to retailers yet.
      // TODO: hook into /api/admin/deliveries once that data is wired.
      return Boolean(driver.deliveriesCompleted && driver.deliveriesCompleted > 0);
    case "all":
    default:
      return true;
  }
}

// ── Page module-level state (per mount) ──────────────────────────────────────

interface PageState {
  drivers: Driver[];
  query: string;
  filter: FilterValue;
  loading: boolean;
  error: string | null;
}

// ── Mount ────────────────────────────────────────────────────────────────────

const driversPage: Page = {
  name: "drivers",
  label: "Manage Drivers",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const abort = new AbortController();
    const state: PageState = {
      drivers: [],
      query: "",
      filter: "all",
      loading: true,
      error: null,
    };

    // Build static structure once; re-render swaps innerHTML of dynamic regions.
    container.innerHTML = `
      <section class="drivers-root" aria-labelledby="drivers-title">
        <header class="drivers-header">
          <h1 id="drivers-title">Manage Drivers</h1>
          <p>
            Each driver gets their own login to
            <a href="/driver" target="_blank" rel="noopener">thekitchenos.io/driver</a>.
          </p>
          <p class="drivers-note">Assigned deliveries appear automatically when confirmed.</p>
        </header>

        <div class="drivers-toolbar" role="toolbar" aria-label="Driver filters">
          <input
            class="drivers-search"
            type="search"
            placeholder="Search by name, vehicle or reg…"
            aria-label="Search drivers"
            data-role="search"
          />
          <select class="drivers-filter" aria-label="Filter drivers" data-role="filter">
            <option value="all">All drivers</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive / on leave</option>
            <option value="with_jobs">With assigned jobs</option>
          </select>
          <button type="button" class="drivers-btn" data-role="open-portal">
            Open Driver Portal →
          </button>
          <button type="button" class="drivers-btn drivers-btn-primary" data-role="add">
            + Add driver
          </button>
        </div>

        <div class="drivers-stats" data-role="stats" aria-live="polite"></div>
        <div class="drivers-list" data-role="list" aria-live="polite"></div>

        <dialog class="drivers-modal" data-role="modal" aria-labelledby="drivers-modal-title">
          <form class="drivers-modal-form" method="dialog" data-role="modal-form">
            <div class="drivers-modal-head">
              <span class="drivers-modal-title" id="drivers-modal-title">Add Driver</span>
              <button type="button" class="drivers-modal-close" data-role="modal-close" aria-label="Close">
                ×
              </button>
            </div>
            <div class="drivers-modal-body">
              <div class="drivers-field">
                <label for="drivers-input-name">Full name *</label>
                <input id="drivers-input-name" name="name" type="text" autocomplete="name"
                  placeholder="Driver's full name" required />
              </div>
              <div class="drivers-field">
                <label for="drivers-input-phone">Mobile number *</label>
                <input id="drivers-input-phone" name="phone" type="tel" autocomplete="tel"
                  placeholder="e.g. 07700 900 123" required />
              </div>
              <div class="drivers-field-row">
                <div class="drivers-field">
                  <label for="drivers-input-vehicle">Vehicle type</label>
                  <input id="drivers-input-vehicle" name="vehicle" type="text"
                    placeholder="e.g. Transit Van" />
                </div>
                <div class="drivers-field">
                  <label for="drivers-input-reg">Reg number</label>
                  <input id="drivers-input-reg" name="reg" type="text" placeholder="e.g. AB12 CDE" />
                </div>
              </div>
              <div class="drivers-field">
                <label for="drivers-input-email">Email (optional)</label>
                <input id="drivers-input-email" name="email" type="email" autocomplete="email"
                  placeholder="e.g. james@example.com" />
              </div>
              <div class="drivers-modal-err" data-role="modal-err" role="alert"></div>
            </div>
            <div class="drivers-modal-foot">
              <button type="button" class="drivers-btn" data-role="modal-cancel">Cancel</button>
              <button type="submit" class="drivers-btn drivers-btn-primary" data-role="modal-submit">
                Add driver
              </button>
            </div>
          </form>
        </dialog>
      </section>
    `;

    // ── Element refs ───────────────────────────────────────────────────────
    const searchEl = container.querySelector<HTMLInputElement>('[data-role="search"]')!;
    const filterEl = container.querySelector<HTMLSelectElement>('[data-role="filter"]')!;
    const addBtn = container.querySelector<HTMLButtonElement>('[data-role="add"]')!;
    const portalBtn = container.querySelector<HTMLButtonElement>('[data-role="open-portal"]')!;
    const statsEl = container.querySelector<HTMLDivElement>('[data-role="stats"]')!;
    const listEl = container.querySelector<HTMLDivElement>('[data-role="list"]')!;
    const modalEl = container.querySelector<HTMLDialogElement>('[data-role="modal"]')!;
    const modalFormEl = container.querySelector<HTMLFormElement>('[data-role="modal-form"]')!;
    const modalCloseEl = container.querySelector<HTMLButtonElement>('[data-role="modal-close"]')!;
    const modalCancelEl = container.querySelector<HTMLButtonElement>('[data-role="modal-cancel"]')!;
    const modalSubmitEl = container.querySelector<HTMLButtonElement>('[data-role="modal-submit"]')!;
    const modalErrEl = container.querySelector<HTMLDivElement>('[data-role="modal-err"]')!;

    // ── Renderers ──────────────────────────────────────────────────────────
    function renderStats(): void {
      const total = state.drivers.length;
      const activeCount = state.drivers.filter(isActive).length;
      const inactiveCount = total - activeCount;

      statsEl.innerHTML = `
        <div class="drivers-stat">
          <div class="drivers-stat-val drivers-stat-val--total">${total}</div>
          <div class="drivers-stat-label">Total drivers</div>
        </div>
        <div class="drivers-stat">
          <div class="drivers-stat-val drivers-stat-val--active">${activeCount}</div>
          <div class="drivers-stat-label">Active</div>
        </div>
        <div class="drivers-stat">
          <div class="drivers-stat-val drivers-stat-val--inactive">${inactiveCount}</div>
          <div class="drivers-stat-label">Inactive</div>
        </div>
      `;
    }

    function renderList(): void {
      if (state.loading) {
        listEl.innerHTML = `<div class="drivers-loading">Loading drivers…</div>`;
        return;
      }
      if (state.error) {
        listEl.innerHTML = `<div class="drivers-error">${escapeHtml(state.error)}</div>`;
        return;
      }

      const filtered = state.drivers
        .filter((d) => matchesFilter(d, state.filter))
        .filter((d) => matchesSearch(d, state.query));

      if (filtered.length === 0) {
        if (state.drivers.length === 0) {
          listEl.innerHTML = `
            <div class="drivers-empty">
              No drivers yet. Click <strong>+ Add driver</strong> to get started.
            </div>
          `;
        } else {
          listEl.innerHTML = `
            <div class="drivers-empty">
              No drivers match your search or filter.
            </div>
          `;
        }
        return;
      }

      listEl.innerHTML = filtered
        .map((d) => {
          const active = isActive(d);
          const vehicle = vehicleLabel(d);
          return `
            <article class="drivers-card" data-driver-id="${escapeHtml(d.id)}">
              <div class="drivers-card-top">
                <div class="drivers-card-id">
                  <div class="drivers-avatar" aria-hidden="true">${escapeHtml(initialsFor(d.name || ""))}</div>
                  <div>
                    <h3 class="drivers-card-name">${escapeHtml(d.name || "Unnamed driver")}</h3>
                    <div class="drivers-card-phone">${escapeHtml(d.phone || "")}</div>
                  </div>
                </div>
                <span class="drivers-status drivers-status--${active ? "active" : "inactive"}">
                  ${active ? "Active" : "Inactive"}
                </span>
              </div>
              ${
                vehicle
                  ? `<div class="drivers-card-vehicle">${escapeHtml(vehicle)}</div>`
                  : ""
              }
              <div class="drivers-card-actions">
                <button type="button" class="drivers-btn" data-action="toggle">
                  ${active ? "Set inactive" : "Set active"}
                </button>
                <button type="button" class="drivers-btn" data-action="edit">Edit</button>
                <button type="button" class="drivers-btn drivers-btn-danger" data-action="remove">
                  Remove
                </button>
              </div>
            </article>
          `;
        })
        .join("");
    }

    function renderAll(): void {
      renderStats();
      renderList();
    }

    // ── Data loading ───────────────────────────────────────────────────────
    async function loadDrivers(): Promise<void> {
      state.loading = true;
      state.error = null;
      renderList();

      try {
        // Retailer-scoped roster. The admin route /admin/drivers requires
        // a kos_admin cookie (platform-admin only); retailer-portal users
        // hit /api/portal/drivers which is gated by the kos_portal cookie
        // and filters by retailerId — see bug fix 2026-05-19.
        const resp = await ctx.api<DriversResponse>("/portal/drivers", {
          signal: abort.signal,
        });
        state.drivers = Array.isArray(resp?.drivers) ? resp.drivers : [];
        state.loading = false;
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError") return;
        state.loading = false;
        if (err instanceof ApiError) {
          state.error =
            err.status === 401 || err.status === 403
              ? "You are not authorised to view drivers."
              : `Couldn't load drivers (HTTP ${err.status}).`;
        } else {
          state.error = "Couldn't load drivers — please try again.";
        }
      }
      renderAll();
    }

    // ── Modal helpers ──────────────────────────────────────────────────────
    function openModal(): void {
      modalErrEl.textContent = "";
      modalFormEl.reset();
      // Native dialog gracefully degrades — only call showModal if available.
      if (typeof modalEl.showModal === "function") {
        modalEl.showModal();
      } else {
        modalEl.setAttribute("open", "");
      }
      // Defer focus until after the dialog is laid out.
      window.requestAnimationFrame(() => {
        const nameInput = modalEl.querySelector<HTMLInputElement>("#drivers-input-name");
        nameInput?.focus();
      });
    }

    function closeModal(): void {
      if (modalEl.open) {
        modalEl.close();
      } else {
        modalEl.removeAttribute("open");
      }
    }

    async function submitNewDriver(ev: SubmitEvent): Promise<void> {
      ev.preventDefault();
      modalErrEl.textContent = "";

      const data = new FormData(modalFormEl);
      const name = String(data.get("name") ?? "").trim();
      const phone = String(data.get("phone") ?? "").trim();
      const vehicle = String(data.get("vehicle") ?? "").trim();
      const reg = String(data.get("reg") ?? "").trim().toUpperCase();
      const email = String(data.get("email") ?? "").trim();

      if (!name || !phone) {
        modalErrEl.textContent = "Name and mobile number are required.";
        return;
      }

      modalSubmitEl.disabled = true;
      const originalLabel = modalSubmitEl.textContent;
      modalSubmitEl.textContent = "Saving…";

      try {
        const resp = await ctx.api<CreateDriverResponse>("/drivers/create", {
          method: "POST",
          body: JSON.stringify({ name, phone, vehicle, reg, email }),
          signal: abort.signal,
        });
        if (!resp?.ok || !resp.driver) {
          modalErrEl.textContent = resp?.error || "Couldn't add driver — please try again.";
          modalSubmitEl.disabled = false;
          modalSubmitEl.textContent = originalLabel;
          return;
        }
        closeModal();
        ctx.showToast(`Driver ${resp.driver.name} added — login SMS sent.`, "success");
        // Optimistic insert; immediately followed by a refresh from the server
        // so we pick up server-side normalisation (id, vehicle shape, etc.).
        state.drivers = [...state.drivers, resp.driver];
        renderAll();
        await loadDrivers();
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError") return;
        if (err instanceof ApiError) {
          modalErrEl.textContent =
            err.message || `Couldn't add driver (HTTP ${err.status}).`;
        } else {
          modalErrEl.textContent = "Network error — please try again.";
        }
        modalSubmitEl.disabled = false;
        modalSubmitEl.textContent = originalLabel;
      }
    }

    // ── Event handlers ─────────────────────────────────────────────────────
    function onSearchInput(ev: Event): void {
      state.query = (ev.target as HTMLInputElement).value.trim();
      renderList();
    }

    function onFilterChange(ev: Event): void {
      state.filter = (ev.target as HTMLSelectElement).value as FilterValue;
      renderList();
    }

    function onAddClick(): void {
      openModal();
    }

    function onPortalClick(): void {
      window.open("/driver", "_blank", "noopener");
    }

    function onListClick(ev: MouseEvent): void {
      const target = ev.target as HTMLElement | null;
      const btn = target?.closest<HTMLButtonElement>("button[data-action]");
      if (!btn) return;
      const card = btn.closest<HTMLElement>(".drivers-card");
      const id = card?.dataset.driverId;
      if (!id) return;
      const action = btn.dataset.action;
      const driver = state.drivers.find((d) => d.id === id);
      if (!driver) return;

      if (action === "toggle") {
        // TODO: wire to PATCH /api/admin/drivers/:id once endpoint exists.
        ctx.showToast(
          "Activate / deactivate isn't wired to the server yet.",
          "info",
        );
      } else if (action === "edit") {
        // TODO: wire to PUT /api/admin/drivers/:id once endpoint exists.
        ctx.showToast(
          "Editing existing drivers will be available once the backend is ready.",
          "info",
        );
      } else if (action === "remove") {
        // TODO: wire to DELETE /api/admin/drivers/:id once endpoint exists.
        void (async () => {
          const ok = await confirmDialog(`Remove driver ${driver.name}?`, {
            title: "Remove driver",
            confirmLabel: "Remove",
            destructive: true,
          });
          if (!ok) return;
          ctx.showToast(
            "Driver removal isn't wired to the server yet.",
            "info",
          );
        })();
      }
    }

    function onModalCancel(): void {
      closeModal();
    }

    function onDialogCancel(ev: Event): void {
      // ESC key — let dialog close, but avoid form-submit side effects.
      ev.preventDefault();
      closeModal();
    }

    // ── Wire it up ─────────────────────────────────────────────────────────
    searchEl.addEventListener("input", onSearchInput);
    filterEl.addEventListener("change", onFilterChange);
    addBtn.addEventListener("click", onAddClick);
    portalBtn.addEventListener("click", onPortalClick);
    listEl.addEventListener("click", onListClick);
    modalCloseEl.addEventListener("click", onModalCancel);
    modalCancelEl.addEventListener("click", onModalCancel);
    modalFormEl.addEventListener("submit", submitNewDriver);
    modalEl.addEventListener("cancel", onDialogCancel);

    // Initial render and fetch.
    renderAll();
    void loadDrivers();

    // ── Cleanup ────────────────────────────────────────────────────────────
    return () => {
      abort.abort();
      searchEl.removeEventListener("input", onSearchInput);
      filterEl.removeEventListener("change", onFilterChange);
      addBtn.removeEventListener("click", onAddClick);
      portalBtn.removeEventListener("click", onPortalClick);
      listEl.removeEventListener("click", onListClick);
      modalCloseEl.removeEventListener("click", onModalCancel);
      modalCancelEl.removeEventListener("click", onModalCancel);
      modalFormEl.removeEventListener("submit", submitNewDriver);
      modalEl.removeEventListener("cancel", onDialogCancel);
      if (modalEl.open) modalEl.close();
    };
  },
};

registerPage(driversPage);

export default driversPage;
